// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Contributors Page
// Route: /contributors
// apps/web/src/routes/contributors.tsx
//
// Human developers and AI agents tracked across projects. Shows expertise
// domains, hours logged, session counts, hourly rates. Create/edit
// contributors with expertise profiles.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  EngineTooltip,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useContributors, useContributorMutations } from '../lib/api/hooks-extended';
import type { Contributor, ContributorType, CreateContributorRequest } from '../lib/api/types-extended';

export const Route = createFileRoute('/contributors')({
  component: ContributorsPage,
});

// ─── Contributor Type Config ────────────────────────────────────────────────

const TYPE_CONFIG: Record<ContributorType, { color: string; icon: string; label: string }> = {
  Human: { color: C.cyan, icon: '👤', label: 'Human' },
  AI_Claude: { color: C.violet, icon: '🟣', label: 'Claude' },
  AI_GPT4: { color: C.neon, icon: '🟢', label: 'GPT-4' },
  AI_Grok: { color: C.amber, icon: '🟡', label: 'Grok' },
  AI_Gemini: { color: C.blue, icon: '🔵', label: 'Gemini' },
  AI_Custom: { color: C.pink, icon: '🔮', label: 'Custom AI' },
};

// ─── Main Component ─────────────────────────────────────────────────────────

function ContributorsPage() {
  const { data: contributors, loading, refetch } = useContributors();
  const mutations = useContributorMutations();
  const [showCreate, setShowCreate] = useState(false);

  const metrics = useMemo(() => {
    if (!contributors) return null;
    const humans = contributors.filter((c) => c.type === 'Human');
    const ais = contributors.filter((c) => c.type !== 'Human');
    const totalHours = contributors.reduce((s, c) => s + (c.totalHours || 0), 0);
    const totalSessions = contributors.reduce((s, c) => s + (c.sessionCount || 0), 0);
    return { total: contributors.length, humans: humans.length, ais: ais.length, totalHours, totalSessions };
  }, [contributors]);

  const countTotal = useCounter(metrics?.total ?? 0, 500, 200);
  const countHours = useCounter(Math.round(metrics?.totalHours ?? 0), 700, 300);

  if (loading) {
    return (
      <EnginePageShell title="Contributors" subtitle="TEAM" icon="👥" accentColor={C.cyan}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[0,1,2,3].map((i) => <GlassPanel key={i} delay={i*0.08}><LoadingShimmer height={50} /></GlassPanel>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {[0,1,2,3].map((i) => <GlassPanel key={i} delay={0.4+i*0.08}><LoadingShimmer height={100} /></GlassPanel>)}
        </div>
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell
      title="Contributors"
      subtitle="TEAM"
      icon="👥"
      accentColor={C.cyan}
      actions={
        <button
          onClick={() => setShowCreate(true)}
          style={{
            background: `${C.neon}15`,
            border: `1px solid ${C.neon}40`,
            color: C.neon,
            fontFamily: F.mono,
            fontSize: 11,
            fontWeight: 600,
            padding: '6px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            letterSpacing: '0.08em',
          }}
        >
          + ADD CONTRIBUTOR
        </button>
      }
    >
      {/* ── Metric Cards ──────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 24 }}>
        <MetricCard label="Total" value={countTotal} icon="👥" color={C.cyan}
          tooltip={<div>Human developers + AI agents tracked.</div>} />
        <MetricCard label="Humans" value={metrics?.humans ?? 0} icon="👤" color={C.neon}
          tooltip={<div>Human team members.</div>} />
        <MetricCard label="AI Agents" value={metrics?.ais ?? 0} icon="🤖" color={C.violet}
          tooltip={<div>AI models used in development sessions.</div>} />
        <MetricCard label="Total Hours" value={countHours} icon="⏱" color={C.amber}
          subtitle={`${metrics?.totalSessions ?? 0} sessions`}
          tooltip={<div>Total hours logged across all contributors.</div>} />
      </div>

      {/* ── Contributors Grid ─────────────────────────────────────────── */}
      {!contributors || contributors.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No contributors yet"
          subtitle="Add human team members and AI agents to track their sessions, expertise, and contribution to your projects."
          action={
            <button
              onClick={() => setShowCreate(true)}
              style={{
                background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
                fontFamily: F.mono, fontSize: 12, padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
              }}
            >
              + ADD CONTRIBUTOR
            </button>
          }
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
          {contributors.map((contrib, i) => (
            <ContributorCard key={contrib.id} contributor={contrib} index={i} />
          ))}
        </div>
      )}

      {/* ── Create Modal ──────────────────────────────────────────────── */}
      {showCreate && (
        <CreateContributorModal
          onClose={() => setShowCreate(false)}
          onCreate={async (data) => {
            const result = await mutations.create(data);
            if (result) { setShowCreate(false); refetch(); }
          }}
          loading={mutations.loading}
        />
      )}
    </EnginePageShell>
  );
}

// ─── ContributorCard ────────────────────────────────────────────────────────

function ContributorCard({ contributor, index }: { contributor: Contributor; index: number }) {
  const [hovered, setHovered] = useState(false);
  const cfg = TYPE_CONFIG[contributor.type] || TYPE_CONFIG.Human;
  const delay = 0.3 + index * 0.06;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? C.cardHover : C.surface,
        border: `1px solid ${hovered ? `${cfg.color}30` : C.faint}`,
        borderRadius: 12,
        padding: 18,
        position: 'relative',
        overflow: 'hidden',
        opacity: 0,
        transform: 'translateY(12px)',
        animation: `engine-feed-in 0.4s cubic-bezier(0.16,1,0.3,1) ${delay}s forwards`,
        transition: 'background 0.2s, border-color 0.2s',
      }}
    >
      {/* Accent glow */}
      <div
        style={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 80,
          height: 80,
          background: `radial-gradient(circle, ${cfg.color}08 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header — name + type */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 24 }}>{cfg.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{contributor.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <StatusBadge label={cfg.label} color={cfg.color} />
              {contributor.role && (
                <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>{contributor.role}</span>
              )}
              {!contributor.isActive && (
                <StatusBadge label="INACTIVE" color={C.coral} />
              )}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <EngineTooltip content={<div>Total sessions this contributor has participated in.</div>}>
            <div>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>SESSIONS</div>
              <div style={{ fontSize: 18, fontFamily: F.mono, fontWeight: 700, color: C.amber }}>
                {contributor.sessionCount ?? 0}
              </div>
            </div>
          </EngineTooltip>
          <EngineTooltip content={<div>Total hours logged across all sessions.</div>}>
            <div>
              <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>HOURS</div>
              <div style={{ fontSize: 18, fontFamily: F.mono, fontWeight: 700, color: C.cyan }}>
                {(contributor.totalHours ?? 0).toFixed(0)}
              </div>
            </div>
          </EngineTooltip>
          {contributor.hourlyRate > 0 && (
            <EngineTooltip content={<div>Hourly rate for cost calculations.</div>}>
              <div>
                <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>RATE</div>
                <div style={{ fontSize: 18, fontFamily: F.mono, fontWeight: 700, color: C.neon }}>
                  ${contributor.hourlyRate}
                </div>
              </div>
            </EngineTooltip>
          )}
        </div>

        {/* Expertise domains */}
        {contributor.expertiseDomains && contributor.expertiseDomains.length > 0 && (
          <div>
            <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
              EXPERTISE
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {contributor.expertiseDomains.map((domain) => {
                const years = contributor.expertiseYears?.[domain];
                return (
                  <EngineTooltip
                    key={domain}
                    content={years ? <div>{domain}: {years} years experience</div> : <div>{domain}</div>}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        fontFamily: F.mono,
                        color: C.violet,
                        background: `${C.violet}10`,
                        border: `1px solid ${C.violet}18`,
                        padding: '2px 7px',
                        borderRadius: 4,
                      }}
                    >
                      {domain}
                      {years ? (
                        <span style={{ color: C.dim, marginLeft: 4 }}>{years}y</span>
                      ) : null}
                    </span>
                  </EngineTooltip>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CreateContributorModal ─────────────────────────────────────────────────

function CreateContributorModal({
  onClose,
  onCreate,
  loading,
}: {
  onClose: () => void;
  onCreate: (data: CreateContributorRequest) => void;
  loading: boolean;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<ContributorType>('Human');
  const [role, setRole] = useState('');
  const [rate, setRate] = useState('');
  const [domains, setDomains] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
    fontSize: 13, outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 9, fontFamily: F.mono, letterSpacing: '0.12em',
    textTransform: 'uppercase' as const, color: C.muted, marginBottom: 6, display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'engine-tooltip-in 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.panel, border: `1px solid ${C.cyan}20`, borderRadius: 16,
          padding: 28, width: '100%', maxWidth: 480,
        }}
      >
        <SectionLabel color={C.cyan}>Add Contributor</SectionLabel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Luke Edson, Claude 3.5 Sonnet" style={inputStyle} autoFocus />
          </div>

          <div>
            <label style={labelStyle}>Type *</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(Object.keys(TYPE_CONFIG) as ContributorType[]).map((t) => {
                const cfg = TYPE_CONFIG[t];
                return (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    style={{
                      background: type === t ? `${cfg.color}20` : 'transparent',
                      border: `1px solid ${type === t ? `${cfg.color}50` : C.faint}`,
                      color: type === t ? cfg.color : C.muted,
                      fontFamily: F.mono, fontSize: 10, padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                    }}
                  >
                    {cfg.icon} {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Role</label>
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g., Senior Engineer, AI Assistant" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Hourly Rate ($)</label>
            <input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="150" type="number" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Expertise Domains (comma-separated)</label>
            <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="backend, frontend, security, AI, infrastructure" style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted, fontFamily: F.mono, fontSize: 11, padding: '8px 20px', borderRadius: 6, cursor: 'pointer' }}>
            CANCEL
          </button>
          <button
            disabled={!name.trim() || loading}
            onClick={() => onCreate({
              name: name.trim(),
              type,
              role: role.trim() || undefined,
              hourlyRate: rate ? parseFloat(rate) : undefined,
              expertiseDomains: domains.split(',').map(d => d.trim()).filter(Boolean),
            })}
            style={{
              background: name.trim() ? `${C.neon}20` : `${C.dim}10`,
              border: `1px solid ${name.trim() ? `${C.neon}40` : C.faint}`,
              color: name.trim() ? C.neon : C.dim,
              fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '8px 24px',
              borderRadius: 6, cursor: name.trim() ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'ADDING...' : 'ADD CONTRIBUTOR'}
          </button>
        </div>
      </div>
    </div>
  );
}
